import { useContext, useEffect, useState, useCallback } from 'react';
import { FaWifi, FaSync, FaExclamationTriangle } from 'react-icons/fa';
import AppContext from '../../store/app-context';
import useLogisticsApi, { OFFLINE_QUEUE_CHANGED_EVENT } from '../../hooks/use-logistics-api';
import useOfflineSync from '../../hooks/use-offline-sync';
import { useCourierCacheInvalidation, useWarehouseIncomingInvalidation, useWarehouseOutgoingInvalidation } from '../../hooks/use-queries';
import useOCR from '../../hooks/use-ocr';

import './Connectivity.scss';

const Connectivity = () => {
    const appCtx = useContext(AppContext);
    const { getQueuedEventsCount, getQueuedEventsBreakdown } = useLogisticsApi();
    const { runSync } = useOfflineSync();
    const { invalidateCourierCache } = useCourierCacheInvalidation();
    const { invalidateWarehouseIncoming } = useWarehouseIncomingInvalidation();
    const { invalidateWarehouseOutgoing } = useWarehouseOutgoingInvalidation();
    const { getQueuedOCRCount } = useOCR();

    const [queuedCount, setQueuedCount] = useState(0);
    const [ocrQueuedCount, setOcrQueuedCount] = useState(0);
    const [breakdown, setBreakdown] = useState(() => getQueuedEventsBreakdown());
    const [syncing, setSyncing] = useState(false);

    // Update queued counts periodically AND on queue-changed events (live updates)
    useEffect(() => {
        const updateCount = () => {
            setQueuedCount(getQueuedEventsCount());
            setOcrQueuedCount(getQueuedOCRCount());
            setBreakdown(getQueuedEventsBreakdown());
        };

        updateCount();
        const interval = setInterval(updateCount, 5000);
        window.addEventListener(OFFLINE_QUEUE_CHANGED_EVENT, updateCount);
        return () => {
            clearInterval(interval);
            window.removeEventListener(OFFLINE_QUEUE_CHANGED_EVENT, updateCount);
        };
    }, [getQueuedEventsCount, getQueuedOCRCount, getQueuedEventsBreakdown]);

    // Total pending count (status + OCR)
    const totalPending = queuedCount + ocrQueuedCount;

    // Auto-sync when coming back online
    useEffect(() => {
        if (appCtx.isOnline && totalPending > 0) {
            handleSync();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appCtx.isOnline]);

    const handleSync = useCallback(async () => {
        if (syncing || !appCtx.isOnline) return;

        setSyncing(true);
        try {
            const result = await runSync();
            setQueuedCount(getQueuedEventsCount());
            setOcrQueuedCount(getQueuedOCRCount());
            setBreakdown(getQueuedEventsBreakdown());
            // Only invalidate if this sync actually processed items
            // (Layout.tsx may have already synced on the same online transition)
            if (result && result.processed > 0) {
                invalidateCourierCache();
                invalidateWarehouseIncoming();
                invalidateWarehouseOutgoing();
            }
        } catch (error) {
            console.error('Sync failed:', error);
        } finally {
            setSyncing(false);
        }
    }, [syncing, appCtx.isOnline, runSync, getQueuedEventsCount, getQueuedOCRCount, getQueuedEventsBreakdown, invalidateCourierCache, invalidateWarehouseIncoming, invalidateWarehouseOutgoing]);

    const breakdownLabel = [
        breakdown.status > 0 && `${breakdown.status} scan`,
        breakdown.signature > 0 && `${breakdown.signature} signature`,
        breakdown.addBox > 0 && `${breakdown.addBox} add-box`,
        breakdown.removeBox > 0 && `${breakdown.removeBox} remove-box`,
        breakdown.routeOrder > 0 && `${breakdown.routeOrder} route`,
        ocrQueuedCount > 0 && `${ocrQueuedCount} OCR`,
    ].filter(Boolean).join(', ');

    // Don't show anything if online and no queued events
    if (appCtx.isOnline && totalPending === 0) {
        return null;
    }

    return (
        <div className={`connectivity ${appCtx.isOnline ? 'online' : 'offline'}`}>
            {!appCtx.isOnline && (
                <div className="connectivity-status offline-banner">
                    <FaExclamationTriangle />
                    <span>Offline - Changes will sync when connected</span>
                </div>
            )}
            
            {totalPending > 0 && (
                <div className="connectivity-queue">
                    <span className="queue-count" title={breakdownLabel}>
                        {totalPending} pending
                        {breakdownLabel && (
                            <span className="queue-breakdown"> ({breakdownLabel})</span>
                        )}
                    </span>
                    {appCtx.isOnline && (
                        <button
                            onClick={handleSync}
                            disabled={syncing}
                            className="sync-button"
                        >
                            <FaSync className={syncing ? 'spinning' : ''} />
                            {syncing ? 'Syncing...' : 'Sync Now'}
                        </button>
                    )}
                </div>
            )}

            {appCtx.isOnline && totalPending === 0 && (
                <div className="connectivity-status online-indicator">
                    <FaWifi />
                    <span>Connected</span>
                </div>
            )}
        </div>
    );
};

export default Connectivity;
